
import { supabase } from "@/integrations/supabase/client";
import { areIntervalsOverlapping, format } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';

// Fetch all teams, optionally filtered by team ID
export const fetchTeams = async (teamId?: string | null) => {
  let teamsQuery = supabase.from('teams').select('id, name');
  
  if (teamId) {
    teamsQuery = teamsQuery.eq('id', teamId);
  }
  
  const { data: teams, error: teamsError } = await teamsQuery;
    
  if (teamsError) {
    console.error('Error fetching teams:', teamsError);
    throw teamsError;
  }
  
  return teams || [];
};

// Clear existing alerts for the specified week
export const clearExistingAlerts = async (formattedWeekStart: string, teamId?: string | null) => {
  let deleteQuery = supabase
    .from('schedule_alerts')
    .delete()
    .eq('week_start_date', formattedWeekStart);
    
  if (teamId) {
    deleteQuery = deleteQuery.eq('team_id', teamId);
  }
  
  return deleteQuery;
};

// Fetch all team members
export const fetchAllTeamMembers = async () => {
  const { data: allTeamMembers, error: teamMembersError } = await supabase
    .from('team_members')
    .select('profile_id, team_id');
    
  if (teamMembersError) {
    console.error('Error fetching team members:', teamMembersError);
    throw teamMembersError;
  }
  
  return allTeamMembers || [];
};

// Fetch team members for a specific team
export const fetchTeamMembers = async (teamId: string) => {
  const { data: teamMembers } = await supabase
    .from('team_members')
    .select('profile_id')
    .eq('team_id', teamId);
    
  return teamMembers?.map(member => member.profile_id) || [];
};

// Fetch guard profiles by IDs
export const fetchGuardProfiles = async (guardIds: string[]) => {
  if (guardIds.length === 0) return [];
  
  const { data: guardProfiles } = await supabase
    .from('profiles')
    .select('id, first_name, last_name, full_name')
    .in('id', guardIds);
  
  return guardProfiles || [];
};

// Fetch schedules for a team in the given date range
export const fetchTeamSchedules = async (teamId: string, weekStartDate: Date, weekEndDate: Date) => {
  const { data: schedules } = await supabase
    .from('team_schedules')
    .select('*')
    .eq('team_id', teamId)
    .or(`start_date.lte.${weekEndDate.toISOString()},end_date.gte.${weekStartDate.toISOString()}`);
  
  // Filter out schedules that don't overlap with the week
  const overlappingSchedules = schedules?.filter(schedule => 
    areIntervalsOverlapping(
      { start: fromLocalISOString(schedule.start_date), end: fromLocalISOString(schedule.end_date) },
      { start: weekStartDate, end: weekEndDate },
      { inclusive: true }
    )
  ) || [];
  
  return overlappingSchedules;
};

// Create a new alert
export const createAlert = async (
  type: string, 
  teamId: string, 
  guardId: string, 
  weekStart: string, 
  hours: number,
  content: string,
  otherTeamHours?: any,
  totalHours?: number
) => {
  try {
    const { data, error } = await supabase
      .from('schedule_alerts')
      .insert({
        alert_type: type as any, // Type assertion to bypass the strict enum check
        team_id: teamId,
        guard_id: guardId || null,
        week_start_date: weekStart,
        hours_scheduled: hours,
        content: content,
        is_resolved: false,
        other_team_hours: otherTeamHours || null,
        total_hours: totalHours || null
      })
      .select('*')
      .single();
    
    if (error) {
      console.error('Error creating alert:', error);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Error in createAlert:', error);
    return null;
  }
};

// Validate guard IDs against existing profiles
export const validateGuardIds = async (guardIds: string[]): Promise<string[]> => {
  if (guardIds.length === 0) return [];
  
  const { data: validGuards } = await supabase
    .from('profiles')
    .select('id')
    .in('id', guardIds);
    
  return validGuards?.map(guard => guard.id) || [];
};

// Check if a team's schedule is visible for a specific week
export const checkTeamVisibility = async (teamId: string, weekStartDate: Date): Promise<boolean> => {
  const formattedWeekStart = format(weekStartDate, 'yyyy-MM-dd');
  
  const { data, error } = await supabase
    .from('week_visibility')
    .select('is_visible')
    .eq('team_id', teamId)
    .eq('week_start_date', formattedWeekStart)
    .single();
  
  if (error) {
    // If no record found, default to visible
    if (error.code === 'PGRST116') {
      return true;
    }
    console.error('Error checking team visibility:', error);
    return true; // Default to visible on error
  }
  
  return data?.is_visible ?? true; // Default to visible if no record
};
